import React, { useState } from 'react';

import { Control, Controller } from 'react-hook-form';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Chip,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import MarkdownEditor from '~/components/Common/MarkdownEditor';

import { TourOfferOptionalExperience } from '~/types/tours/tourOffer';

import NativeTourImageList from './NativeTourImageList';
import { NativeTourForm, NativeTourOptionalExperience } from './nativeTourForm';

interface Props {
  control: Control<NativeTourForm>;
  experiences: Array<TourOfferOptionalExperience>;
  leadDays: number | null | undefined;
}

const NO_TRANSFER = 'none';
// More time slots than this are counted in the row; the full list is in the opened experience.
const LISTED_TIMES = 3;
const TRANSFERS = { arrival: 'Arrival transfer', departure: 'Departure transfer' } as const;

const isInvalid = (experience: NativeTourOptionalExperience) =>
  !experience.name.trim() || experience.photos.some((photo) => !photo);

/** Optional experiences customers can add: Salesforce sets them up, Admin describes them. One open at a time. */
export default function NativeTourOptionalExperiencesSection({ control, experiences, leadDays }: Props) {
  const [openId, setOpenId] = useState<string | false>(false);

  if (experiences.length === 0) {
    return <Alert severity="info">This tour has no optional experiences. They come from Salesforce.</Alert>;
  }

  return (
    <Stack spacing={2}>
      <Alert severity="info">
        {leadDays ? `Customers can add these up to ${leadDays} days before departure. ` : ''}
        Days, times and duration come from Salesforce.
      </Alert>
      <Controller
        control={control}
        name="optionalExperiences"
        render={({ field }) => (
          <Box>
            {field.value.map((experience, index) => {
              const source = experiences.find((candidate) => candidate.id === experience.id);
              const update = (change: Partial<NativeTourOptionalExperience>) =>
                field.onChange(field.value.map((other, i) => (i === index ? { ...other, ...change } : other)));
              const days = source?.dayNumbers.length ? `Day ${source.dayNumbers.join(', ')}` : null;
              const timeSlots = source?.timeSlots ?? [];
              const times = timeSlots.length > 0 ? timeSlots.join(', ') : null;
              const timesLabel = timeSlots.length > LISTED_TIMES ? `${timeSlots.length} time slots` : times;
              return (
                <Accordion
                  key={experience.id}
                  disableGutters
                  variant="outlined"
                  expanded={openId === experience.id}
                  onChange={(_event, isOpen) => setOpenId(isOpen ? experience.id : false)}
                  slotProps={{ transition: { unmountOnExit: true } }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{ '& .MuiAccordionSummary-content': { minWidth: 0 } }}
                  >
                    <Stack
                      direction={{ xs: 'column', md: 'row' }}
                      spacing={1}
                      alignItems={{ md: 'center' }}
                      justifyContent="space-between"
                      width="100%"
                      minWidth={0}
                      pr={1}
                    >
                      <Typography fontWeight={500} sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                        {experience.name || 'Untitled experience'}
                      </Typography>
                      <Stack direction="row" gap={1} flexWrap="wrap" justifyContent={{ md: 'flex-end' }} flexShrink={0}>
                        {days && <Chip size="small" variant="outlined" label={days} />}
                        {timesLabel && <Chip size="small" variant="outlined" label={timesLabel} />}
                        {experience.isTransfer && (
                          <Chip size="small" variant="outlined" label={TRANSFERS[experience.isTransfer]} />
                        )}
                        <Chip
                          size="small"
                          variant="outlined"
                          label={`${experience.photos.length} photo${experience.photos.length === 1 ? '' : 's'}`}
                        />
                        {!experience.description && <Chip size="small" color="warning" label="No description" />}
                        {isInvalid(experience) && <Chip size="small" color="error" label="Needs fixing" />}
                      </Stack>
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Stack spacing={2}>
                      <Grid container spacing={2}>
                        <Grid size={{ xs: 12, md: 8 }}>
                          <TextField
                            size="small"
                            label="Name"
                            value={experience.name}
                            onChange={(event) => update({ name: event.target.value })}
                            error={!experience.name.trim()}
                            helperText={!experience.name.trim() ? 'Required' : undefined}
                            required
                            fullWidth
                          />
                        </Grid>
                        <Grid size={{ xs: 12, md: 4 }}>
                          <TextField
                            select
                            size="small"
                            label="Airport transfer"
                            value={experience.isTransfer ?? NO_TRANSFER}
                            onChange={(event) =>
                              update({
                                isTransfer:
                                  event.target.value === NO_TRANSFER
                                    ? null
                                    : (event.target.value as 'arrival' | 'departure'),
                              })
                            }
                            fullWidth
                          >
                            <MenuItem value={NO_TRANSFER}>Not a transfer</MenuItem>
                            {(Object.keys(TRANSFERS) as Array<keyof typeof TRANSFERS>).map((transfer) => (
                              <MenuItem key={transfer} value={transfer}>
                                {TRANSFERS[transfer]}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                        <Grid size={12}>
                          <Typography variant="caption" color="text.secondary" display="block">
                            From Salesforce
                          </Typography>
                          <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                            {[days, times, source?.duration ? `Duration ${source.duration}` : null]
                              .filter(Boolean)
                              .join(' · ') || 'No days or times'}
                          </Typography>
                        </Grid>
                      </Grid>
                      <MarkdownEditor
                        label="Description"
                        showLabel
                        value={experience.description ?? ''}
                        onChange={(description) => update({ description: description || null })}
                      />
                      <Box>
                        <Typography variant="subtitle2" mb={1}>
                          Photos
                        </Typography>
                        <NativeTourImageList
                          value={experience.photos.map((svcImageId) => ({ svcImageId, caption: null }))}
                          onChange={(photos) => update({ photos: photos.map(({ svcImageId }) => svcImageId) })}
                          withCaptions={false}
                          emptyText="No photos."
                        />
                      </Box>
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Box>
        )}
      />
    </Stack>
  );
}
